import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import { DataTable, DataTableColumn, ActionsDropdown } from '@/shared/components';
import { TextCell } from '@/shared/components/TextCell/TextCell';
import { dateValidators } from '@/shared';
import { CustomerDataTableProps, CustomerRecord } from '../../models/customer.model';
import { SharedAlertDialog, useAlertDialog } from '@/shared/components';
import { useCustomerMutations } from '../../queries/customers.query';

export const CustomerDataTable: React.FC<CustomerDataTableProps> = ({
  customers,
  loading,
  pagination,
  currentFilters,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onFilterChange,
  onClickCustomer,
  filterToolbarSlot,
  filterToolbarTrailingSlot,
  filterToolbarActiveCount,
  onClearFilterToolbar,
}: Readonly<CustomerDataTableProps>) => {
  const { t } = useTranslation('customers');
  const navigate = useNavigate();
  const cancelSubscriptionDialog = useAlertDialog();
  const { cancelSubscription } = useCustomerMutations();
  const [selectedCustomer, setSelectedCustomer] = React.useState<CustomerRecord | null>(null);

  const handleCancelSubscriptionConfirm = async () => {
    if (!selectedCustomer) return;
  
    try {
      await cancelSubscription.mutateAsync(selectedCustomer.Id);
      cancelSubscriptionDialog.setIsOpen(false);
      setSelectedCustomer(null);  
    } catch {
      console.error('Failed to delete customer');
    }
  };
  

  const formatEnergy = useCallback((amount: number) => {
    return (
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount) + ' kWh'
    );
  }, []);

  const renderCustomerCell = useCallback(
    (value?: string | null) => <TextCell value={value ?? '-'} truncate />,
    []
  );

  const renderDeviceStatusCell = useCallback(
    (row: CustomerRecord) => {
      if (row.DeviceState === null) {
        return renderCustomerCell('-');
      }
      return renderCustomerCell(row.DeviceState);
    },
    [renderCustomerCell]
  );

  /** Actions Column */
  const renderActionsCell = useCallback(
    (customer: CustomerRecord) => {
 const handleAction = (action: string) => {
  switch (action) {
    case 'view':
      navigate({ to: `/customers/${customer.Id}` });
      break;
    case 'edit':
      navigate({ to: `/customers/${customer.Id}/edit` });
      break;
    case 'delete':
      setSelectedCustomer(customer);
      cancelSubscriptionDialog.setIsOpen(true);
      break;
    default:
      break;
  }
};

      const now = new Date();
      const endDate = customer.SubEndDate ? new Date(customer.SubEndDate) : null;
      const isActive = endDate ? endDate > now : false;
      const isCancelled = customer.IsSubscriptionCancelled; 


      const actions = [
        {
          id: 'view',
          label: t('list.actions.view', 'View Details'),
          icon: <Eye size={16} />,
          onClick: () => handleAction('view'),
        },
        {
          id: 'edit',
          label: t('list.actions.edit', 'Edit Customer'),
          icon: <Pencil size={16} />,
          onClick: () => handleAction('edit'),
        },
      ];
      
      if (isActive && !isCancelled) {
        actions.push({
          id: 'delete',
          label: t('list.actions.delete', 'Delete Customer'),
          icon: <Trash2 size={16} />,
          onClick: () => handleAction('delete'),
        });
      }
      
      return <ActionsDropdown actions={actions} className="hover:cursor-pointer" />;
    },
    [navigate, t]
  );

  const columns: DataTableColumn<CustomerRecord>[] = useMemo(
    () => [
      {
        id: 'FirstName',
        header: t('list.columns.firstName', 'First Name'),
        cell: row => renderCustomerCell(row.FirstName),
        width: '150px',
        sortable: true,
      },
      {
        id: 'LastName',
        header: t('list.columns.lastName', 'Last Name'),
        cell: row =>
          row.LastName ? (
            renderCustomerCell(row.LastName)
          ) : (
            <div className="flex items-center">
              {renderCustomerCell()}
              <button
                type="button"
                onClick={e => {
                  // Prevent the row click handler from firing when user clicks the marker
                  e.stopPropagation();
                  // navigate to edit page for this customer
                  // uses the same route format as elsewhere in the app
                  navigate({ to: `/customers/${row.Id}/edit` });
                }}
                title={t('list.lastNameMissing', 'Last name missing')}
                aria-label={t('list.lastNameMissing', 'Last name missing')}
                className="ml-2 inline-flex items-center px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 font-medium text-xs border border-yellow-200 cursor-pointer hover:bg-yellow-200 hover:text-yellow-900 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-yellow-300"
              >
                !
              </button>
            </div>
          ),
        width: '150px',
        sortable: true,
      },
      {
        id: 'StreetAddress',
        header: t('list.columns.street', 'Street'),
        cell: row => renderCustomerCell(row.StreetAddress),
        width: '200px',
        sortable: true,
      },
      {
        id: 'ZipCode',
        header: t('list.columns.postalCode', 'Postal Code'),
        cell: row => renderCustomerCell(row.ZipCode),
        width: '100px',
        sortable: true,
      },
      {
        id: 'City',
        header: t('list.columns.city', 'City'),
        cell: row => renderCustomerCell(row.City),
        width: '150px',
        sortable: true,
      },
      {
        id: 'DeviceState',
        header: t('list.columns.deviceStatus', 'Device State'),
        cell: renderDeviceStatusCell,
        width: '150px',
        sortable: false,
      },
      {
        id: 'EnergyBalance',
        header: t('list.columns.energyBalance', 'Energy Balance'),
        cell: row => renderCustomerCell(formatEnergy(row.EnergyBalance)),
        width: '150px',
        sortable: false,
        clientSideSort: true,
      },
      {
        id: 'SubscriptionStatus',
        header: t('list.columns.subscriptionStatus', 'Subscription Status'),
        cell: row => {
          if (!row.SubEndDate) return renderCustomerCell('-');
          const endDate = new Date(row.SubEndDate);
          const now = new Date();
          const isActive = endDate > now;
          return renderCustomerCell(
            isActive
              ? t('list.status.active', 'Active')
              : t('list.status.inactive', 'Inactive')
          );
        },
        width: '120px',
        sortable: false,
      },
      {
        id: 'SubscriptionEndDate',
        header: t('list.columns.subscriptionEndDate', 'Subscription End Date'),
        cell: row =>
          renderCustomerCell(
            row.SubEndDate && dateValidators.isValid(row.SubEndDate)
              ? new Date(row.SubEndDate).toLocaleDateString()
              : '-'
          ),
        width: '150px',
        sortable: false,
      },
      {
        id: 'MeterStatus',
        header: t('list.columns.meterStatus', 'Meter Status'),
        cell: row => renderCustomerCell(row.MeterStatus),
        width: '150px',
        sortable: false,
      },
      {
        id: 'actions',
        header: t('list.columns.actions', 'Actions'),
        cell: renderActionsCell,
        width: '120px',
      },
    ],
    [
      formatEnergy,
      t,
      renderCustomerCell,
      renderDeviceStatusCell,
      navigate,
      renderActionsCell,
    ]
  );

  const filterDefs = useMemo(
    () => [
      {
        id: 'nameOrEmailSearch',
        label: t('list.filtersLabels.nameOrEmail', 'Name or email'),
        type: 'text' as const,
        placeholder: t('list.filtersPlaceholders.nameOrEmail', 'Search by name or email...'),
      },
      {
        id: 'citySearch',
        label: t('list.filtersLabels.city', 'City'),
        type: 'text' as const,
        placeholder: t('list.filtersPlaceholders.city', 'Enter city'),
      },
      {
        id: 'zipCodeSearch',
        label: t('list.filtersLabels.postalCode', 'Postal code'),
        type: 'text' as const,
        placeholder: t(
          'list.filtersPlaceholders.postalCode',
          'Enter postal code'
        ),
      },

      {
        id: 'status',
        label: t('list.filtersLabels.profileStatus'),
        type: 'select' as const,
        options: [
          { label: t('list.all'), value: 'all' },
          { label: t('list.active'), value: "active" },
          { label: t('list.inactive'), value:"deleted" },
        ],
        placeholder: t('list.filtersLabels.status'), 
      },
    ],
    [t]
  );

  return (
    <div data-testid="customer-data-table">
      {/* Notice explaining yellow-highlighted rows */}
      <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground justify-end w-full">
        <div className="flex items-center gap-2">
          <div className="text-right">
            {t(
              'list.missingLastNameNotice',
              'Yellow rows indicate customers without a last name'
            )}
          </div>
          <span className="inline-flex items-center px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 font-medium text-xs border border-yellow-200">
            !
          </span>
        </div>
      </div>

      <DataTable<CustomerRecord>
        data={customers as CustomerRecord[]}
        rowClassName={(row: CustomerRecord) =>
          !row.LastName ? 'bg-yellow-50' : ''
        }
        columns={columns}
        loading={loading}
        onRowClick={onClickCustomer ? item => onClickCustomer(item) : undefined}
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortChange={onSortChange}
        onFilterChange={onFilterChange}
        filters={filterDefs}
        initialFilters={currentFilters}
        filterToolbarSlot={filterToolbarSlot}
        filterToolbarTrailingSlot={filterToolbarTrailingSlot}
        filterToolbarActiveCount={filterToolbarActiveCount}
        onClearFilterToolbar={onClearFilterToolbar}
        showPagination={true}
        showFilters={true}
        emptyMessage={t('list.noResults', 'No customers found')}
        pageSizeOptions={[5, 10, 20, 50]}
        className="bg-white"
      />



     <SharedAlertDialog
        open={cancelSubscriptionDialog.isOpen}
        onOpenChange={cancelSubscriptionDialog.setIsOpen}
        title={
          t('detail.cancelSubscriptionConfirmationTitle') ?? 'Delete Customer?'
        }
        description={
          t('detail.cancelSubscriptionConfirmationDesc') ??
          "This will cancel the customer's subscription. This action cannot be undone."
        }
        confirmText={t('detail.cancelSubscription') ?? 'Delete Customer'}
        variant="destructive"
        onConfirm={handleCancelSubscriptionConfirm}
      />


    </div>
  );
};

export default CustomerDataTable;
